import { ChangeDetectionStrategy, Component, input, output, ViewEncapsulation } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { Item } from "../../../pojo/Item";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { AnswerType } from "../../data-ingestion/data.ingestion.service";
import {
    createEmptyWorkflowCustAtrbMappingRow,
    custAtrbIdFromDropdownItem,
    WorkflowCustAtrbMappingUiRow,
} from "./workflow.cust.atrb.mappings.util";

@Component({
    standalone: true,
    selector: "s25-ng-workflow-custom-attribute-mappings",
    template: `
        <div class="workflow-ca-mappings">
            <label class="workflow-ca-mappings__label">{{ title() }}</label>
            <small class="workflow-ca-mappings__help form-text">
                Map custom attributes to field references (e.g. $CNET-123-STRING).
            </small>
            @for (row of mappings(); track row.rowKey) {
                <div class="workflow-ca-mapping-row">
                    <s25-custom-attr-dropdown
                        class="workflow-ca-mapping-row__dropdown"
                        [itemTypeId]="itemTypeId()"
                        [filter]="workflowCustomAttributeFilter"
                        [value]="row.dropdownChoice ?? undefined"
                        [ariaLabel]="'Custom attribute'"
                        (valueChange)="onCustAtrbPicked(row.rowKey, $event)"
                    />
                    <input
                        type="text"
                        class="form-control form-control--inline"
                        [ngModel]="row.valueRef"
                        (ngModelChange)="onValueRefChange(row.rowKey, $event)"
                        placeholder="e.g. $CNET-123-STRING"
                    />
                    <button
                        type="button"
                        class="workflow-ca-mapping-row__remove"
                        (click)="removeRow(row.rowKey)"
                        title="Remove"
                    >
                        ×
                    </button>
                </div>
            }
            <button type="button" class="workflow-ca-mapping-row__add" (click)="addRow()">Add mapping</button>
        </div>
    `,
    styles: `
        .workflow-ca-mappings {
            margin-bottom: 16px;
        }

        .workflow-ca-mappings__label {
            display: block;
            margin-bottom: 4px;
            font-weight: 500;
            color: var(--color--text-strong);
        }

        .workflow-ca-mappings__help {
            color: var(--color--text-muted);
            font-size: 12px;
            margin-top: 4px;
            margin-bottom: 8px;
            display: block;
        }

        .workflow-ca-mapping-row {
            display: flex;
            gap: 8px;
            align-items: center;
            margin-bottom: 8px;
        }

        .workflow-ca-mapping-row__dropdown {
            flex: 1;
            min-width: 0;
            display: block;
            position: relative;
        }

        .form-control--inline {
            flex: 1;
            min-width: 0;
            padding: 8px 12px;
            border: 1px solid var(--color--form-border);
            border-radius: 4px;
            font-size: 14px;
            background: var(--color--form-bg);
            color: var(--color--text-strong);
        }

        .workflow-ca-mapping-row__remove {
            flex-shrink: 0;
            width: 32px;
            height: 34px;
            padding: 0;
            border: 1px solid var(--color--border);
            border-radius: 4px;
            background: var(--color--surface-2-bg);
            font-size: 18px;
            line-height: 1;
            cursor: pointer;
            color: var(--color--text-muted);
        }

        .workflow-ca-mapping-row__remove:hover {
            background: var(--color--surface-2-bg-hover);
            color: var(--color--text-strong);
        }

        .workflow-ca-mapping-row__add {
            margin-top: 4px;
            padding: 6px 12px;
            border: 1px solid var(--color--link-text);
            border-radius: 4px;
            background: var(--color--surface-bg);
            color: var(--color--link-text);
            font-size: 14px;
            cursor: pointer;
        }

        .workflow-ca-mapping-row__add:hover {
            background: var(--color--surface-2-bg-hover);
            color: var(--color--link-text-hover);
            border-color: var(--color--link-text-hover);
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [FormsModule, S25DropdownPaginatedModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowCustomAttributeMappingsComponent {
    readonly itemTypeId = input.required<Item.Ids.Event | Item.Ids.Organization>();
    readonly mappings = input.required<WorkflowCustAtrbMappingUiRow[]>();
    readonly title = input<string>("Custom Attribute Mappings");

    readonly mappingsChange = output<WorkflowCustAtrbMappingUiRow[]>();

    readonly workflowCustomAttributeFilter = (item: DropDownItem): boolean =>
        AnswerType.fromAtrbDataType(String(item.custAtrbType)) != null;

    onCustAtrbPicked(rowKey: string, picked: DropDownItem | null): void {
        const custAtrbId = custAtrbIdFromDropdownItem(picked);
        const dropdownChoice = picked && !picked.isNoneItem ? picked : undefined;
        this.updateRow(rowKey, { custAtrbId, dropdownChoice });
    }

    onValueRefChange(rowKey: string, valueRef: string): void {
        this.updateRow(rowKey, { valueRef });
    }

    addRow(): void {
        this.mappingsChange.emit([...this.mappings(), createEmptyWorkflowCustAtrbMappingRow()]);
    }

    removeRow(rowKey: string): void {
        this.mappingsChange.emit(this.mappings().filter((row) => row.rowKey !== rowKey));
    }

    private updateRow(rowKey: string, patch: Partial<WorkflowCustAtrbMappingUiRow>): void {
        this.mappingsChange.emit(this.mappings().map((row) => (row.rowKey === rowKey ? { ...row, ...patch } : row)));
    }
}
